import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ConjugateConnectionGraphQL,
  LinkedMoleculeConnectionGraphQL,
  ConjugateGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {isConnectionToMolecule} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {
  getMoleculeSideAttachmentText,
  getSourceMoleculeSideAttachmentText,
  getSourceMonomerSideAttachmentText,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsTableConjugateStructureUtil';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AttachmentPointProps {
  connection: ConjugateConnectionGraphQL | LinkedMoleculeConnectionGraphQL;
}

export function AttachmentPoint({connection}: AttachmentPointProps): JSX.Element | null {
  const {source, target} = connection;
  const sourceSideText = isConnectionToMolecule(source)
    ? getSourceMoleculeSideAttachmentText(source)
    : getSourceMonomerSideAttachmentText(source);
  const targetMoleculeText = getMoleculeSideAttachmentText(target.rGroup);
  return (
    <span>
      {targetMoleculeText != null && <span className={css(styles.attachment)}>{targetMoleculeText}</span>}
      {sourceSideText != null && (
        <>
          <BenchlingIcon icon="minus-large" />
          <span className={css(styles.attachment)}>{sourceSideText}</span>
        </>
      )}
    </span>
  );
}

interface ComplexPolymerConjugateProps {
  conjugate: ConjugateGraphQL;
}

export function ComplexPolymerConjugate({conjugate}: ComplexPolymerConjugateProps): JSX.Element {
  const {entity, linkedMolecules} = conjugate;

  return (
    <div>
      <BioentityChip
        className={css(styles.moleculeEntity)}
        id={isInaccessible(entity) ? null : entity.id}
        size={ChipSize.SMALL}
      />
      <AttachmentPoint connection={conjugate.connection} />
      {linkedMolecules &&
        linkedMolecules.map((linkedMolecule, idx) => {
          const {id, entity, connection} = linkedMolecule;

          return (
            <div key={id}>
              <BioentityChip
                className={css(styles.moleculeEntity)}
                id={isInaccessible(entity) ? null : entity.id}
                size={ChipSize.SMALL}
              />
              {connection && <AttachmentPoint connection={connection} />}
            </div>
          );
        })}
    </div>
  );
}

export interface ComplexPolymerConjugateStructureProps {
  conjugates: Array<ConjugateGraphQL>;
}

export default function ComplexPolymerConjugateStructure({
  conjugates,
}: ComplexPolymerConjugateStructureProps): JSX.Element {
  return (
    <div>
      {conjugates.map((conjugate, idx) => (
        <div key={conjugate.id}>
          {idx > 0 && <hr />}
          <ComplexPolymerConjugate conjugate={conjugate} />
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  moleculeEntity: {
    margin: 5,
    marginLeft: 0,
  },
  attachment: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginLeft: 5,
    marginRight: 5,
  },
});
